import {
  TeContainer,
  TeContainerSlim,
  TeButton,
  TeDivider,
  TeContextMenuItem,
  TePagination,
  TeText,
} from '@topcon/tuicl-react';
import { TePageChangeEvent } from '@topcon/tuicl';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PlaceholderContainer, RightDrawer, useOutsideClick } from '@topcon/topcon-utils';

import UserFilters from './UserFilters';
import { useFilterService } from '../../../providers/filter/filter.context';
import { FilterIDs } from '../../../providers/filter/filter.types';
import UsersTable from './UsersTable';
import InvitationModal from './InvitationModal';
import SuggestedUsersCallout from './SuggestedUsersCallout';
import ViewContainer from '../../../components/ViewContainer';
import TableHeader from '../TableHeader';
import UserProfile from './UserProfile';
import UserRemoveModal from './UserRemoveModal';
import { useInvitations, useUsers } from '../userManagement.queries';
import { CombinedUser, Status } from '../types';
import { useAppService } from '../../../providers/app/app.context';

function Users() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Seeds SuggestedUsersCallout's initial open state for the one-shot
  // `?suggestedUsers=open` deep link from the Overview review action (see ADR
  // 0005). Captured once via lazy initializer so later searchParams updates
  // (e.g. the strip below) don't reopen it.
  const [suggestedUsersInitialOpen] = useState(() => searchParams.get('suggestedUsers') === 'open');
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [userRemoveModalOpen, setUserRemoveModalOpen] = useState(false);
  const [userConfirmModalOpen, setUserConfirmModalOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [editMode, setEditMode] = useState(false);
  const { data: users, isFetched: loadedUsers, refetch: refetchUsers } = useUsers();
  const { data: invitations, refetch: refetchInvites } = useInvitations();
  const { identity, isOwner } = useAppService();
  const filterID = FilterIDs.UserFilters;
  const filterService = useFilterService();
  const filters = useMemo(() => {
    return filterService.getFilters(filterID);
  }, [filterID, filterService]);
  const { t } = useTranslation('users');

  const combinedUsers: CombinedUser[] = useMemo(() => {
    const usersArr: CombinedUser[] = [];

    // Process regular users from items
    users?.items?.forEach((u) => {
      const {
        identity: userIdentity,
        identityId,
        businessPartner,
        businessPartnerId,
        id,
        createdAt,
        profileIds,
      } = u;
      if (!userIdentity) return;
      const { firstName, lastName, email, archived } = userIdentity;
      usersArr.push({
        name: `${firstName} ${lastName}`,
        email,
        businessPartner,
        businessPartnerId,
        id,
        identityId,
        profileIds,
        archived,
        createdAt,
        isOwner: false,
      });
    });

    // Merge owner if it exists
    if (users?.owner && identity) {
      const { owner } = users;
      const { identity: ownerIdentity } = owner;

      if (ownerIdentity) {
        // Check if owner already exists as a user in items
        const existingOwnerUser = users.items?.find((u) => u.identityId === owner.identityId);

        // Combine profiles from owner and user (if user exists)
        // Owner has virtual administrator profile, user may have additional profiles
        // We combine both to show the full set of profiles
        const ownerProfileIds = owner.profileIds || [];
        const userProfileIds = existingOwnerUser?.profileIds || [];
        // Union of both arrays to get all profiles (owner virtual + user actual)
        const combinedProfileIds = Array.from(new Set([...ownerProfileIds, ...userProfileIds]));

        const { firstName, lastName, email } = ownerIdentity;
        const ownerName = `${firstName || ''} ${lastName || ''}`.trim();
        // If owner exists as a user, use owner's name from owner object if user's name is empty
        const existingOwnerUserName = existingOwnerUser?.identity
          ? `${existingOwnerUser.identity.firstName || ''} ${existingOwnerUser.identity.lastName || ''}`.trim()
          : '';
        // Prefer owner's name if user's name is empty or whitespace
        const finalName = existingOwnerUserName || ownerName;
        const ownerUser: CombinedUser = {
          name: finalName,
          email: email || existingOwnerUser?.identity?.email || '',
          businessPartner: undefined,
          businessPartnerId: owner.businessPartnerId,
          id: existingOwnerUser?.id || owner.identityId, // Use user id if exists, otherwise use identityId
          identityId: owner.identityId,
          profileIds: combinedProfileIds,
          createdAt: existingOwnerUser?.createdAt || new Date().toISOString(),
          archived: existingOwnerUser?.identity?.archived || false,
          isOwner: true,
        };

        // If owner exists as a user, replace it with the merged version
        if (existingOwnerUser) {
          const index = usersArr.findIndex((u) => u.identityId === owner.identityId);
          if (index !== -1) {
            usersArr[index] = ownerUser;
          }
        } else {
          // Otherwise, add owner as a new entry
          usersArr.push(ownerUser);
        }
      }
    }

    // Add invitations
    invitations?.forEach((i) => {
      usersArr.push({
        ...i,
        name: i.email,
        status: Status.Invited,
        isOwner: false,
      });
    });

    return usersArr;
  }, [invitations, users, identity]);

  // TODO: These need to be reworked once we get proper profiles
  const filteredUsers = useMemo(() => {
    const lowerSearch = searchValue.toLowerCase();
    const statusFilters = (filters.find(({ name, active }) => active && name === 'status')?.value ??
      []) as string[];
    const profileFilters = (filters.find(({ name, active }) => active && name === 'profile')
      ?.value ?? []) as string[];
    const [addedFrom, addedTo] = ['addedFrom', 'addedTo']
      .map(
        (field) =>
          filters.find(({ name, active }) => active && name === field)?.value as string | undefined
      )
      .map((d) => (d ? new Date(d) : undefined));

    const result = combinedUsers.filter((user) => {
      const { name = '', email = '', status, profileIds, createdAt } = user;
      const searchMatches =
        name.toLowerCase().includes(lowerSearch) || email.toLowerCase().includes(lowerSearch);

      // NOTE: For mvp purposes assumes all statuses are similar and can be lowercased and don't have multi-part
      const statusMatches =
        statusFilters.length === 0 ||
        (status === undefined && statusFilters.includes(Status.Active.toLowerCase())) ||
        (status !== undefined && statusFilters.includes(status.toLowerCase()));
      const profileMatches =
        profileFilters.length === 0 || profileIds.some((id) => profileFilters.includes(id));
      const fromMatches = !addedFrom || new Date(createdAt).getTime() >= addedFrom.getTime();
      const toMatches = !addedTo || new Date(createdAt).getTime() <= addedTo.getTime();
      return searchMatches && statusMatches && profileMatches && fromMatches && toMatches;
    });
    return result;
  }, [combinedUsers, searchValue, filters]);

  // --- Pagination ---
  const pageSize = 50;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(filteredUsers.length / pageSize);
  const safePage = Math.min(currentPage, totalPages) || 1;
  const pagedUsers = filteredUsers.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Reset to page 1 when filters/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, filters]);

  // Consume the `?suggestedUsers=open` deep link once so a refresh, back
  // navigation, or bookmark of this URL doesn't reopen the modal.
  useEffect(() => {
    if (searchParams.has('suggestedUsers')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('suggestedUsers');
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const visibleUsers = pagedUsers?.length ?? 0;
  const totalUsers = combinedUsers?.length ?? 0;
  const selectedUsersCount = selectedIds.length;
  const selectedUsers = useMemo(
    () => combinedUsers.filter(({ id }) => selectedIds.includes(id)),
    [combinedUsers, selectedIds]
  );
  const removableUsers = useMemo(
    () => selectedUsers.filter((u) => !u.isOwner && u.identityId !== identity?.id),
    [selectedUsers, identity]
  );
  const noContent = !loadedUsers || !totalUsers || !visibleUsers;
  const profileInfoRef = useOutsideClick(() => !userConfirmModalOpen && setSelectedId(''));

  const renderLoader = () => {
    return <PlaceholderContainer hasLoader text={t('loadingUsers')} />;
  };

  const renderNoUsers = () => {
    return (
      <PlaceholderContainer
        icon="groups"
        text={t('noUsersAdded')}
        secondaryText={t('addNewUserDescription')}
      >
        <TeButton text={t('inviteUser')} size="s" teclick={() => setInviteModalOpen(true)} />
      </PlaceholderContainer>
    );
  };

  const renderNoContent = () => {
    if (!loadedUsers) return renderLoader();
    if (!totalUsers) return renderNoUsers();
    return <PlaceholderContainer text={t('noSearchResults')} />;
  };

  const handleInvitationModalClose = (shouldUpdate: boolean) => {
    setInviteModalOpen(false);
    if (shouldUpdate) refetchInvites();
  };

  const handleUserRemoveModalClose = (shouldUpdate: boolean) => {
    setUserRemoveModalOpen(false);
    if (shouldUpdate) {
      setSelectedIds([]);
      refetchInvites();
      refetchUsers();
    }
  };

  const handleUserEdit = (id: string) => {
    setEditMode(true);
    setSelectedId(id);
  };

  const handleUserSelect = (id: string) => {
    setEditMode(false);
    setSelectedId(id);
  };

  const handleUserDrawerClose = (shouldUpdate: boolean) => {
    handleUserSelect('');
    if (shouldUpdate) {
      refetchInvites();
      refetchUsers();
    }
  };

  const isEditAllowed = () => {
    const [user] = selectedUsers;
    if (selectedUsers.length !== 1 || user.isOwner) return false;
    return user.identityId !== identity?.id || !!isOwner;
  };

  const renderContent = () => {
    return (
      <TeContainerSlim
        backgroundColor="background.secondary"
        borderRadius={totalPages > 1 ? undefined : [0, 0, 4, 4]}
        overflow="hidden"
        column
        grow
      >
        {noContent ? (
          renderNoContent()
        ) : (
          <TeContainerSlim fill column overflow="auto">
            <UsersTable
              users={pagedUsers}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              setUserRemoveModalOpen={setUserRemoveModalOpen}
              selectedId={selectedId}
              currentIdentityId={identity?.id}
              onUserSelect={handleUserSelect}
              onUserEdit={handleUserEdit}
              onSortChange={() => setCurrentPage(1)}
              currentLoggedUserIsOwner={isOwner}
            />
            <TeContainerSlim
              height={16}
              shadow="top"
              zIndex="base.bottom"
              backgroundColor="background.primary"
              grow
            />
          </TeContainerSlim>
        )}
      </TeContainerSlim>
    );
  };

  const renderPagination = () => {
    if (totalPages <= 1 || noContent) return null;
    return (
      <TeContainerSlim
        justifyContent="space-between"
        alignItems="center"
        padding={['xs', 'm']}
        backgroundColor="background.primary"
        borderRadius={[0, 0, 4, 4]}
        fill
      >
        <TePagination
          page={safePage}
          pages={totalPages}
          size="m"
          techange={(e: TePageChangeEvent) => setCurrentPage(e.page)}
        />

        <TeText size="s" color="text.secondary">
          {t('pageInfo', {
            from: (safePage - 1) * pageSize + 1,
            to: Math.min(safePage * pageSize, filteredUsers.length),
            total: filteredUsers.length,
          })}
        </TeText>
      </TeContainerSlim>
    );
  };

  const renderActions = () => {
    return (
      <>
        <TeContextMenuItem
          text={t('edit')}
          disabled={!isEditAllowed()}
          teclick={() => handleUserEdit(selectedIds[0])}
        />
        <TeContextMenuItem
          text={t('remove')}
          disabled={removableUsers.length === 0}
          teclick={() => setUserRemoveModalOpen(true)}
        />
      </>
    );
  };

  return (
    <ViewContainer
      title={t('users')}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      visibleItemsText={totalUsers ? t('visibleUsers', { visibleUsers, totalUsers }) : undefined}
      searchPlaceholder={t('searchUsers')}
      createNewItemText={t('inviteUser')}
      onCreateNewItemClick={() => setInviteModalOpen(true)}
      buttonDisabled={!loadedUsers}
    >
      <SuggestedUsersCallout initialOpen={suggestedUsersInitialOpen} />

      <TeContainer
        alignItems="center"
        justifyContent="space-between"
        fill
        backgroundColor="background.primary"
        borderRadius={[4, 4, 0, 0]}
      >
        <TableHeader
          filterID={FilterIDs.UserFilters}
          filters={filters}
          countText={t('selectedUsers', { count: selectedUsersCount })}
          actions={renderActions()}
          actionsDisabled={selectedUsersCount < 1}
        />

        <TeContainerSlim alignItems="center" gap="s" justifyContent="flex-start">
          <TeButton
            size="s"
            icon="filter_alt"
            backgroundColor={filtersOpen ? 'background.selected' : 'background.primary'}
            teclick={() => setFiltersOpen(!filtersOpen)}
            iconColor="icon.primary"
          />
        </TeContainerSlim>
      </TeContainer>

      <TeDivider dividerColor="border.secondary" />

      <TeContainerSlim overflow="hidden" column relative grow>
        {renderContent()}
        <RightDrawer open={filtersOpen}>
          <UserFilters users={combinedUsers} />
        </RightDrawer>
      </TeContainerSlim>
      {renderPagination()}
      <RightDrawer open={!!selectedId} reference={profileInfoRef}>
        {selectedId && (
          <UserProfile
            user={combinedUsers.find((user) => user.id === selectedId)}
            onClose={handleUserDrawerClose}
            editMode={editMode}
            setEditMode={setEditMode}
            isInvitation={!!invitations?.find((user) => user.id === selectedId)}
            userConfirmModalOpen={userConfirmModalOpen}
            setUserConfirmModalOpen={setUserConfirmModalOpen}
            currentLoggedUser={identity}
          />
        )}
      </RightDrawer>

      {inviteModalOpen && <InvitationModal open onClose={handleInvitationModalClose} />}

      <UserRemoveModal
        selectedUsers={removableUsers}
        open={userRemoveModalOpen}
        onClose={handleUserRemoveModalClose}
      />
    </ViewContainer>
  );
}

export default Users;
